import axios from 'axios';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface OpenRouterResponse {
  choices: Array<{
    message: {
      content: string;
      role: string;
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface DocumentSection {
  title: string;
  level: number;
  content?: string;
  subsections?: DocumentSection[];
  contentType: 'detailed' | 'bullet' | 'placeholder' | 'mixed' | 'executive';
  estimatedTokens: number;
}

export interface DocumentStructure {
  title: string;
  sections: DocumentSection[];
  totalEstimatedTokens: number;
}

export interface DocumentContext {
  title: string;
  objective: string;
  targetAudience: string;
  documentType: string;
  keyThemes: string[];
  completedSections: string[];
  currentSection: string;
  documentSummary: string;
}

// Cost-effective model selection with specific hierarchy and fallback logic
const MODEL_CONFIG = {
  // Primary: Google Gemini 2.0 Flash (most cost-effective)
  primary: {
    model: 'google/gemini-2.0-flash-exp',
    maxTokens: 4000,
    temperature: 0.7,
    costPer1kTokens: 0.000075, // $0.000075 per 1K tokens (very cheap!)
    priority: 1
  },
  // Secondary: DeepSeek V3 (good quality, moderate cost)
  secondary: {
    model: 'deepseek-ai/deepseek-coder-v2-instruct',
    maxTokens: 4000,
    temperature: 0.7,
    costPer1kTokens: 0.001, // $0.001 per 1K tokens
    priority: 2
  },
  // Tertiary: OpenAI GPT-4o (highest quality, highest cost)
  tertiary: {
    model: 'openai/gpt-4o',
    maxTokens: 4000,
    temperature: 0.7,
    costPer1kTokens: 0.005, // $0.005 per 1K tokens
    priority: 3
  }
};

// Task-specific model selection with cost control
const TASK_MODEL_MAPPING = {
  // Fast, cheap models for simple tasks
  fast: {
    primary: MODEL_CONFIG.primary,
    fallback: MODEL_CONFIG.secondary
  },
  // Balanced models for standard tasks
  balanced: {
    primary: MODEL_CONFIG.primary,
    fallback: MODEL_CONFIG.secondary
  },
  // High-quality models for complex tasks
  quality: {
    primary: MODEL_CONFIG.primary,
    fallback: MODEL_CONFIG.secondary
  },
  // DeepResearch models (highest quality needed)
  deepResearch: {
    primary: MODEL_CONFIG.secondary,
    fallback: MODEL_CONFIG.tertiary
  }
};

class OpenRouterService {
  private apiKey: string;
  private baseURL: string = 'https://openrouter.ai/api/v1';

  constructor() {
    this.apiKey = process.env.REACT_APP_OPENROUTER_API_KEY || '';
    
    if (!this.apiKey) {
      console.error('OpenRouter API key not found. Please check your .env file.');
    } else {
      console.log('OpenRouter API key loaded successfully');
    }
  }

  // Get the most cost-effective model for the task with fallback logic
  private getModelConfig(taskType: 'fast' | 'balanced' | 'quality' | 'deepResearch' = 'balanced') {
    return TASK_MODEL_MAPPING[taskType];
  }

  // Try to use a model with fallback logic
  private async tryModelWithFallback(
    messages: ChatMessage[],
    taskType: 'fast' | 'balanced' | 'quality' | 'deepResearch' = 'balanced',
    customMaxTokens?: number
  ): Promise<OpenRouterResponse> {
    const config = this.getModelConfig(taskType);
    const maxTokens = customMaxTokens || config.primary.maxTokens;

    // Try primary model first
    try {
      console.log(`Trying primary model: ${config.primary.model}`);
      return await this.makeRequest(messages, config.primary, maxTokens);
    } catch (error) {
      console.warn(`Primary model failed: ${config.primary.model}, trying secondary: ${config.fallback.model}`);
      
      // Try secondary model
      try {
        const secondaryMaxTokens = customMaxTokens || config.fallback.maxTokens;
        return await this.makeRequest(messages, config.fallback, secondaryMaxTokens);
      } catch (secondaryError) {
        console.warn(`Secondary model failed: ${config.fallback.model}, trying tertiary: ${MODEL_CONFIG.tertiary.model}`);
        
        // Try tertiary model as final fallback
        try {
          const tertiaryMaxTokens = customMaxTokens || MODEL_CONFIG.tertiary.maxTokens;
          return await this.makeRequest(messages, MODEL_CONFIG.tertiary, tertiaryMaxTokens);
        } catch (tertiaryError) {
          console.error('All models failed');
          console.error('Primary error:', error);
          console.error('Secondary error:', secondaryError);
          console.error('Tertiary error:', tertiaryError);
          throw new Error(`All available models failed. Please try again later.`);
        }
      }
    }
  }

  // Make the actual API request
  private async makeRequest(
    messages: ChatMessage[],
    modelConfig: any,
    maxTokens: number
  ): Promise<OpenRouterResponse> {
    try {
      console.log(`Making request to OpenRouter with model: ${modelConfig.model}`);
      console.log('Max tokens:', maxTokens);
      
      const response = await axios.post(
        `${this.baseURL}/chat/completions`,
        {
          model: modelConfig.model,
          messages,
          temperature: modelConfig.temperature,
          max_tokens: maxTokens,
          stream: false,
        },
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
            'HTTP-Referer': window.location.origin,
            'X-Title': 'Document Builder',
          },
        }
      );

      console.log('OpenRouter response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error calling OpenRouter API:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      throw new Error(`OpenRouter API error: ${error.response?.data?.error?.message || error.message}`);
    }
  }

  async chatCompletion(
    messages: ChatMessage[],
    taskType: 'fast' | 'balanced' | 'quality' | 'deepResearch' = 'balanced',
    customMaxTokens?: number
  ): Promise<OpenRouterResponse> {
    return this.tryModelWithFallback(messages, taskType, customMaxTokens);
  }

  async streamChatCompletion(
    messages: ChatMessage[],
    taskType: 'fast' | 'balanced' | 'quality' | 'deepResearch' = 'balanced',
    onChunk: (chunk: string) => void,
    customMaxTokens?: number
  ): Promise<void> {
    const config = this.getModelConfig(taskType);
    const maxTokens = customMaxTokens || config.primary.maxTokens;

    // Try primary model first
    try {
      console.log(`Trying streaming with primary model: ${config.primary.model}`);
      await this.makeStreamingRequest(messages, config.primary, maxTokens, onChunk);
    } catch (error) {
      console.warn(`Primary streaming model failed: ${config.primary.model}, trying secondary: ${config.fallback.model}`);
      
      // Try secondary model
      try {
        const secondaryMaxTokens = customMaxTokens || config.fallback.maxTokens;
        await this.makeStreamingRequest(messages, config.fallback, secondaryMaxTokens, onChunk);
      } catch (secondaryError) {
        console.warn(`Secondary streaming model failed: ${config.fallback.model}, trying tertiary: ${MODEL_CONFIG.tertiary.model}`);
        
        // Try tertiary model as final fallback
        try {
          const tertiaryMaxTokens = customMaxTokens || MODEL_CONFIG.tertiary.maxTokens;
          await this.makeStreamingRequest(messages, MODEL_CONFIG.tertiary, tertiaryMaxTokens, onChunk);
        } catch (tertiaryError) {
          console.error('All streaming models failed');
          console.error('Primary error:', error);
          console.error('Secondary error:', secondaryError);
          console.error('Tertiary error:', tertiaryError);
          throw new Error(`All available streaming models failed. Please try again later.`);
        }
      }
    }
  }

  // Make the actual streaming API request
  private async makeStreamingRequest(
    messages: ChatMessage[],
    modelConfig: any,
    maxTokens: number,
    onChunk: (chunk: string) => void
  ): Promise<void> {
    try {
      console.log(`Making streaming request to OpenRouter with model: ${modelConfig.model}`);
      console.log('Max tokens:', maxTokens);
      
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Document Builder',
        },
        body: JSON.stringify({
          model: modelConfig.model,
          messages,
          temperature: modelConfig.temperature,
          max_tokens: maxTokens,
          stream: true,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('OpenRouter streaming error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') return;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                onChunk(content);
              }
            } catch (e) {
              // Ignore parsing errors for incomplete chunks
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error streaming from OpenRouter API:', error);
      throw new Error(`OpenRouter streaming error: ${error.message}`);
    }
  }

  // Generate document structure with enhanced planning
  async generateDocumentStructure(prompt: string): Promise<DocumentStructure> {
    const structurePrompt = `You are a Senior Solution Architect with 15+ years of experience in creating comprehensive, professional documents. Your expertise spans business strategy, technical architecture, and executive communication.

TASK: Create a sophisticated document structure for: "${prompt}"

REQUIREMENTS:
1. Analyze the request and determine the optimal document type and structure
2. Create 4-6 main sections with 2-4 subsections each
3. Assign appropriate content types:
   - 'executive': High-level strategic content for executives
   - 'detailed': Comprehensive technical/business analysis
   - 'bullet': Key points, lists, and actionable items
   - 'mixed': Combination of narrative and structured content
4. Estimate realistic token counts (500-1500 per section)
5. Ensure logical flow and professional document architecture

CONTENT TYPE GUIDELINES:
- Executive sections: Strategic overview, business value, ROI
- Detailed sections: Comprehensive analysis, methodology, technical specifications
- Bullet sections: Key findings, recommendations, action items
- Mixed sections: Narrative with supporting lists and data

Return as JSON with this exact format:
{
  "title": "Professional Document Title",
  "sections": [
    {
      "title": "Section Title",
      "level": 1,
      "contentType": "executive|detailed|bullet|mixed",
      "estimatedTokens": 800,
      "subsections": [
        {
          "title": "Subsection Title", 
          "level": 2,
          "contentType": "executive|detailed|bullet|mixed",
          "estimatedTokens": 500
        }
      ]
    }
  ],
  "totalEstimatedTokens": 6000
}

Create a structure that would impress senior executives and technical stakeholders.`;

    const messages: ChatMessage[] = [
      { role: 'system', content: 'You are a Senior Solution Architect. Return only valid JSON.' },
      { role: 'user', content: structurePrompt }
    ];

    const response = await this.chatCompletion(messages, 'quality', 3000);
    const content = response.choices[0].message.content;
    
    try {
      // Extract JSON from the response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      throw new Error('No valid JSON found in response');
    } catch (error) {
      console.error('Error parsing document structure:', error);
      throw new Error('Failed to generate document structure');
    }
  }

  // Generate sophisticated context-aware prompts
  private generateSophisticatedPrompt(
    section: DocumentSection,
    context: DocumentContext,
    existingContent: string
  ): string {
    const contentTypeInstructions = {
      executive: `Write in an executive summary style with:
- High-level strategic insights and business value
- Clear value propositions and ROI analysis
- Professional tone suitable for C-level executives
- Concise yet comprehensive coverage
- Focus on business impact and strategic implications

IMPORTANT: Use proper HTML formatting with clear structure. Do NOT repeat headings.`,
      
      detailed: `Write comprehensive, detailed content with:
- In-depth analysis and thorough explanations
- Technical specifications and methodologies
- Real-world examples and case studies
- Professional terminology and industry best practices
- Substantial content that demonstrates expertise

IMPORTANT: Use proper HTML formatting with clear structure. Do NOT repeat headings.`,
      
      bullet: `Create structured, actionable content with:
- Clear, concise bullet points and lists
- Key findings and recommendations
- Actionable next steps and deliverables
- Important highlights and takeaways
- Easy-to-scan format for quick reference

IMPORTANT: Use proper HTML formatting with clear structure. Do NOT repeat headings.`,
      
      mixed: `Combine narrative and structured content with:
- Detailed explanations followed by key points
- Comprehensive analysis with supporting lists
- Professional narrative with actionable takeaways
- Balanced approach between detail and clarity
- Both strategic overview and tactical details

IMPORTANT: Use proper HTML formatting with clear structure. Do NOT repeat headings.`
    };

    const contextSummary = context.completedSections.length > 0 
      ? `\n\nDOCUMENT CONTEXT:\n- Document Type: ${context.documentType}\n- Objective: ${context.objective}\n- Target Audience: ${context.targetAudience}\n- Key Themes: ${context.keyThemes.join(', ')}\n- Previously Completed: ${context.completedSections.join(', ')}\n- Current Progress: ${context.documentSummary}`
      : '';

    return `You are a Senior Solution Architect orchestrating the creation of a professional document. You have deep expertise in business strategy, technical architecture, and executive communication.

DOCUMENT OVERVIEW:
Title: ${context.title}
Objective: ${context.objective}
Target Audience: ${context.targetAudience}

CURRENT SECTION: ${section.title}
Content Type: ${section.contentType}
Target Length: ${section.estimatedTokens} words

${contentTypeInstructions[section.contentType as keyof typeof contentTypeInstructions]}

CRITICAL FORMATTING REQUIREMENTS:
1. Use proper HTML formatting (h1, h2, h3, p, ul, ol, li, strong, em)
2. Do NOT repeat section headings or titles - they are already in the document
3. Do NOT include the section title in your response - start directly with content
4. Ensure proper spacing between sections with <p> tags
5. Use <ul> and <li> for bullet points
6. Create substantial, high-quality content that demonstrates expertise
7. Include relevant examples, data points, and industry insights
8. Maintain consistency with the document's professional tone
9. Ensure the content flows logically with previous sections
10. Add value that would impress senior executives and technical stakeholders

EXISTING DOCUMENT CONTENT:${existingContent}${contextSummary}

Create content that is:
- Substantial and comprehensive (not superficial)
- Professionally written with industry expertise
- Relevant and actionable
- Well-structured and easy to follow
- Suitable for the target audience
- Properly formatted with clean HTML structure
- Does NOT include section titles (they are already in the document)

Write the content for "${section.title}" (start directly with content, no title):`;
  }

  // Generate content for a specific section with enhanced context
  async generateSectionContent(
    section: DocumentSection,
    documentContext: DocumentContext,
    existingContent: string
  ): Promise<string> {
    const sophisticatedPrompt = this.generateSophisticatedPrompt(section, documentContext, existingContent);

    const messages: ChatMessage[] = [
      { role: 'system', content: 'You are a Senior Solution Architect with 15+ years of experience. Create professional, high-quality content that demonstrates deep expertise and strategic thinking.' },
      { role: 'user', content: sophisticatedPrompt }
    ];

    const response = await this.chatCompletion(messages, 'quality', section.estimatedTokens + 1000);
    return response.choices[0].message.content;
  }

  // Analyze document context and extract key information
  async analyzeDocumentContext(prompt: string, existingContent: string): Promise<DocumentContext> {
    const analysisPrompt = `You are a Senior Solution Architect analyzing a document request.

DOCUMENT REQUEST: "${prompt}"

EXISTING CONTENT: ${existingContent}

Analyze this request and provide context for document generation. Return as JSON:

{
  "title": "Professional Document Title",
  "objective": "Clear statement of document purpose",
  "targetAudience": "Primary audience (e.g., 'C-level executives', 'Technical stakeholders')",
  "documentType": "Type of document (e.g., 'Business Proposal', 'Technical Specification')",
  "keyThemes": ["theme1", "theme2", "theme3"],
  "completedSections": ["section1", "section2"],
  "currentSection": "current section being worked on",
  "documentSummary": "Brief summary of document progress and content"
}

Focus on professional, business-oriented analysis.`;

    const messages: ChatMessage[] = [
      { role: 'system', content: 'You are a Senior Solution Architect. Return only valid JSON.' },
      { role: 'user', content: analysisPrompt }
    ];

    const response = await this.chatCompletion(messages, 'balanced', 2000);
    const content = response.choices[0].message.content;
    
    try {
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      throw new Error('No valid JSON found in response');
    } catch (error) {
      console.error('Error parsing document context:', error);
      // Return default context
      return {
        title: 'Professional Document',
        objective: 'Create comprehensive document',
        targetAudience: 'Stakeholders',
        documentType: 'Business Document',
        keyThemes: ['strategy', 'implementation'],
        completedSections: [],
        currentSection: 'Introduction',
        documentSummary: 'Document in progress'
      };
    }
  }

  // DeepResearch approach using GPT-4o with enhanced prompting
  async generateDeepResearchDocument(prompt: string, onProgress?: (progress: string) => void): Promise<string> {
    const deepResearchPrompt = `You are a Senior Solution Architect conducting comprehensive research to create a world-class, multi-page document.

DOCUMENT REQUEST: "${prompt}"

You are creating a document that will be presented to senior executives and technical stakeholders. This document should demonstrate:

1. **Strategic Thinking**: High-level business strategy and value propositions
2. **Technical Excellence**: Detailed technical analysis and specifications
3. **Executive Communication**: Clear, professional language suitable for C-level executives
4. **Comprehensive Coverage**: 5-10 pages of substantial, meaningful content
5. **Professional Structure**: Proper document architecture with headers, sections, and subsections
6. **Actionable Insights**: Practical recommendations and next steps
7. **Industry Expertise**: Demonstrate deep knowledge and best practices

CRITICAL FORMATTING REQUIREMENTS:
- Use professional HTML formatting (h1, h2, h3, p, ul, ol, li, strong, em)
- Do NOT repeat section headings or titles
- Do NOT include the section title in your response - start directly with content
- Ensure proper spacing between sections with <p> tags
- Use <ul> and <li> for bullet points
- Include executive summaries, detailed analysis, and actionable recommendations
- Provide comprehensive coverage with substantial content
- Use professional terminology and industry best practices
- Include relevant examples, case studies, and data points
- Create content that would impress senior executives
- Ensure logical flow and professional document structure
- Do NOT create tables - use bullet points and structured lists instead

Create a comprehensive document that represents the highest quality of professional documentation. Make it thorough, detailed, and executive-ready with proper HTML formatting.`;

    const messages: ChatMessage[] = [
      { role: 'system', content: 'You are a Senior Solution Architect with 15+ years of experience. Create world-class, executive-ready documentation with proper HTML formatting. Do NOT repeat headings or titles. Do NOT create tables.' },
      { role: 'user', content: deepResearchPrompt }
    ];

    let fullContent = '';
    
    await this.streamChatCompletion(
      messages,
      'deepResearch',
      (chunk: string) => {
        fullContent += chunk;
        if (onProgress) {
          onProgress(fullContent);
        }
      },
      8000
    );

    return fullContent;
  }
}

const openRouterService = new OpenRouterService();
export default openRouterService; 